/* eslint-disable no-unused-vars */
import { ref, onUnmounted } from 'vue'
import type { ManagedTimeout, ManagedInterval } from '@/customTypes'
import type { Ref } from 'vue'

/**
 * Convert snace_case to camelCase
 * @param {any} data - data to convert to camelCase
 * @returns {any} - data in camelCase
 */
export const convertSnakeToCamelCase = (data: any) => {
  let camelCasedData: any = {}

  if (typeof data === 'object' && data !== null) {
    for (const [key, value] of Object.entries(data)) {
      const camelCaseKey = key.replace(/_([a-z])/g, (match, letter) => {
        return letter.toUpperCase()
      })

      if (Array.isArray(value)) {
        camelCasedData[camelCaseKey] = value.map((item) => {
          return convertSnakeToCamelCase(item)
        })
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        camelCasedData[camelCaseKey] = convertSnakeToCamelCase(value)
      } else {
        camelCasedData[camelCaseKey] = value
      }
    }
  } else {
    camelCasedData = data
  }

  return camelCasedData
}

export const useTimeout = () => {
  const componentTimeouts = ref<Map<ReturnType<typeof setTimeout>, { execute: boolean }>>(new Map()) // Use a Map

  function setManagedTimeout(callback: (...args: any[]) => void, delay: number): ManagedTimeout {
    let execute = true

    const timeoutId = setTimeout(() => {
      if (execute) {
        callback()
      }
      componentTimeouts.value.delete(timeoutId)
    }, delay)

    componentTimeouts.value.set(timeoutId, { execute })

    return {
      clear() {
        execute = false
        clearTimeout(timeoutId)
        componentTimeouts.value.delete(timeoutId)
      }
    }
  }

  function clearManagedTimeout(timeoutRef: Ref<ManagedTimeout | null>) {
    if (timeoutRef.value) {
      timeoutRef.value.clear()
      timeoutRef.value = null
    }
  }

  function clearAllTimeouts() {
    componentTimeouts.value.forEach((value, timeoutId) => {
      value.execute = false
      clearTimeout(timeoutId)
    })
    componentTimeouts.value.clear()
  }

  function createManagedDebounce<T extends (...args: any[]) => any>(
    callback: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    const timeoutRef: Ref<ManagedTimeout | null> = ref(null)

    return (...args: Parameters<T>) => {
      clearManagedTimeout(timeoutRef)

      timeoutRef.value = setManagedTimeout(() => {
        callback(...args)
      }, delay)
    }
  }

  onUnmounted(() => {
    clearAllTimeouts()
  })

  return {
    setManagedTimeout,
    clearManagedTimeout,
    createManagedDebounce
  }
}

export const useInterval = () => {
  const componentIntervals = ref<Set<ReturnType<typeof setInterval>>>(new Set())

  function setManagedInterval(callback: (...args: any[]) => void, delay: number): ManagedInterval {
    const intervalId = setInterval(callback, delay)
    componentIntervals.value.add(intervalId)

    return {
      clear() {
        clearInterval(intervalId)
        componentIntervals.value.delete(intervalId)
      }
    }
  }

  function clearManagedInterval(intervalRef: Ref<ManagedInterval | null>) {
    if (intervalRef.value) {
      intervalRef.value.clear()
      intervalRef.value = null
    }
  }

  function clearAllIntervals() {
    componentIntervals.value.forEach((intervalId) => clearInterval(intervalId))
    componentIntervals.value.clear()
  }

  onUnmounted(() => {
    clearAllIntervals()
  })

  return {
    setManagedInterval,
    clearManagedInterval,
    clearAllIntervals
  }
}
